import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { SmartphoneIcon, ShieldCheckIcon, GlobeIcon } from 'lucide-react';
export function FeaturesDMRV() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28" data-id="element-927">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-928">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-929">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-930">
            <span className="text-carbon-bright" data-id="element-931">Independent verification</span>{' '}
            from field to boardroom
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-932">
            Two platforms, one mission: audit-grade emissions data and
            compliance-ready reporting trusted by regulators, buyers, and
            investors.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={containerVariants} data-id="element-933">
          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-934">
            <svg className="absolute -top-2 -right-2 w-32 h-32 text-carbon-bright opacity-[0.06]" viewBox="0 0 120 120" fill="none" aria-hidden="true" data-id="element-935">
              <rect x="38" y="10" width="44" height="80" rx="8" stroke="currentColor" strokeWidth="3" data-id="element-936" />
              <rect x="48" y="18" width="24" height="50" rx="2" fill="currentColor" fillOpacity="0.3" data-id="element-937" />
              <circle cx="60" cy="80" r="4" fill="currentColor" data-id="element-938" />
              <path d="M80 30 C88 30 94 24 94 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-939" />
              <path d="M80 22 C84 22 88 19 88 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-940" />
              <circle cx="80" cy="30" r="2" fill="currentColor" data-id="element-941" />
            </svg>
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-942">
              <SmartphoneIcon className="w-6 h-6 text-carbon-bright" data-id="element-943" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-944">
              Mobile-First Field dMRV
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-945">
              Farmers and field agents log harvest, cookstove, and land-use data
              via USSD on any phone — no internet required. GPS-tagged entries
              generate audit-grade emissions records at the source.
            </p>
          </motion.article>

          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-946">
            <svg className="absolute -top-3 -right-3 w-36 h-36 text-carbon-bright opacity-[0.06]" viewBox="0 0 120 120" fill="none" aria-hidden="true" data-id="element-947">
              <rect x="15" y="10" width="70" height="90" rx="6" stroke="currentColor" strokeWidth="2.5" data-id="element-948" />
              <line x1="28" y1="30" x2="72" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-949" />
              <line x1="28" y1="42" x2="65" y2="42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-950" />
              <line x1="28" y1="54" x2="58" y2="54" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-951" />
              <circle cx="90" cy="85" r="22" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="2.5" data-id="element-952" />
              <path d="M82 85 L88 91 L100 79" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-953" />
            </svg>
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-954">
              <ShieldCheckIcon className="w-6 h-6 text-carbon-bright" data-id="element-955" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-956">
              Enterprise Carbon Accounting
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-957">
              Automate Scope 1, 2, and 3 GHG inventories for large enterprises.
              Generate IFRS S1/S2, CSRD, and CBAM-compliant reports with a full
              digital audit trail ready for third-party verification.
            </p>
          </motion.article>

          <motion.article className="relative overflow-hidden p-8 rounded-2xl border border-carbon-geo bg-white cursor-pointer" variants={cardVariants} whileHover={{
          y: -6,
          boxShadow: '0 12px 32px -8px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(34, 197, 94, 0.3)',
          transition: {
            duration: 0.25,
            ease: 'easeOut'
          }
        }} data-id="element-958">
            <svg className="absolute -top-4 -right-4 w-40 h-40 text-carbon-bright opacity-[0.06]" viewBox="0 0 140 140" fill="none" aria-hidden="true" data-id="element-959">
              <circle cx="70" cy="65" r="45" stroke="currentColor" strokeWidth="2.5" data-id="element-960" />
              <ellipse cx="70" cy="65" rx="20" ry="45" stroke="currentColor" strokeWidth="1.5" data-id="element-961" />
              <line x1="25" y1="50" x2="115" y2="50" stroke="currentColor" strokeWidth="1.5" data-id="element-962" />
              <line x1="25" y1="80" x2="115" y2="80" stroke="currentColor" strokeWidth="1.5" data-id="element-963" />
              <circle cx="30" cy="115" r="8" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" data-id="element-964" />
              <circle cx="110" cy="115" r="8" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.5" data-id="element-965" />
              <path d="M50 100 L30 115" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" data-id="element-966" />
              <path d="M90 100 L110 115" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" data-id="element-967" />
            </svg>
            <div className="w-12 h-12 rounded-xl bg-carbon-bright/10 flex items-center justify-center mb-6" data-id="element-968">
              <GlobeIcon className="w-6 h-6 text-carbon-bright" data-id="element-969" />
            </div>
            <h3 className="text-xl font-bold text-carbon-deep mb-3" data-id="element-970">
              Global Market Access
            </h3>
            <p className="text-carbon-mid leading-relaxed" data-id="element-971">
              Automated EUDR due diligence, Digital Quality Certificates, and
              ESG disclosures. One platform bridges Africa's producers and
              enterprises with EU and international compliance requirements.
            </p>
          </motion.article>
        </motion.div>
      </div>
    </section>;
}